import React, { useEffect, useState } from 'react';
import { RefreshCw, Eye, Loader2, ArrowLeftRight, ArrowDownToLine, History, RotateCcw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SyncResultPanel } from './sync/SyncResultPanel';
import { SyncHistory } from './sync/SyncHistory';

export function SheetSyncControls({ cfg, setField }) {
  const { syncMembersFromSheet, previewSheetSync, getSheetSyncLog, getSheetSyncLogDetail, resetSheetSyncState } = useAuth();
  const [busy, setBusy] = useState('');
  const [res, setRes] = useState(null);
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const twoWay = cfg.sheet_sync_mode === 'two_way';
  const disabled = !!busy || !cfg.sheet_id || !cfg.service_account_set;

  const loadLog = () => getSheetSyncLog(10).then(setLog).catch(() => setLog([]));
  useEffect(() => { loadLog(); /* eslint-disable-next-line */ }, []);

  const run = async (kind) => {
    setBusy(kind); setRes(null);
    try {
      const out = kind === 'preview' ? await previewSheetSync() : await syncMembersFromSheet();
      setRes(out);
      if (kind === 'sync') loadLog();
    } catch (e) { setRes({ error: e?.response?.data?.detail || 'Sync failed' }); }
    setBusy('');
  };

  const reset = async () => {
    if (!window.confirm('Forget the last-sync snapshot? The next two-way sync will treat every record as new on both sides (last modified wins).')) return;
    await resetSheetSyncState(); setRes({ mode: 'two_way', dry_run: true, summary: {}, details: {} });
  };

  const btn = (bg, fg) => ({ background: bg, color: fg, opacity: disabled ? 0.6 : 1 });
  return (
    <div className="mt-3" data-testid="sheet-sync-controls">
      <label className="text-xs" style={{ color: 'var(--olive)' }}>Sync direction</label>
      <div className="grid md:grid-cols-2 gap-2 items-start">
        <Select value={cfg.sheet_sync_mode || 'pull'} onValueChange={(v) => setField('sheet_sync_mode', v)}>
          <SelectTrigger data-testid="gw-sheet-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pull"><span className="inline-flex items-center gap-2"><ArrowDownToLine size={14} /> Pull from Sheet only (Sheet is the master)</span></SelectItem>
            <SelectItem value="two_way"><span className="inline-flex items-center gap-2"><ArrowLeftRight size={14} /> Two-way — last modified wins</span></SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs" style={{ color: 'var(--olive)' }}>
          {twoWay ? 'Edits made in the app are written back to the Sheet and vice-versa. When both sides changed the same member, the most recent edit wins — but a blank never erases a filled value. Save settings before syncing.'
            : 'Rows from the Sheet create or update members. Nothing is written to the Sheet.'}
        </p>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {twoWay && (
          <button data-testid="gw-sheet-sync-preview" onClick={() => run('preview')} disabled={disabled} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-title" style={btn('var(--sage)', 'var(--ink)')}>
            {busy === 'preview' ? <Loader2 className="animate-spin" size={14} /> : <Eye size={14} />} Preview changes
          </button>
        )}
        <button data-testid="gw-sheet-sync" onClick={() => run('sync')} disabled={disabled} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-title" style={btn('var(--maroon)', 'var(--cream)')}>
          {busy === 'sync' ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />} {twoWay ? 'Run two-way sync now' : 'Sync members from Sheet'}
        </button>
        {twoWay && (
          <button data-testid="gw-sheet-reset-state" onClick={reset} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm" style={{ color: 'var(--olive)', border: '1px solid rgba(0,0,0,0.15)' }}>
            <RotateCcw size={14} /> Forget snapshot
          </button>
        )}
        <button data-testid="gw-sheet-log-toggle" onClick={() => setShowLog((v) => !v)} className="inline-flex items-center gap-2 px-3 py-2 rounded text-sm" style={{ color: 'var(--navy)', border: '1px solid rgba(0,0,0,0.15)' }}>
          <History size={14} /> History ({log.length})
        </button>
      </div>
      {cfg.last_sheet_sync_at && <p className="text-xs mt-2" style={{ color: 'var(--olive)' }} data-testid="gw-last-sync">Last sync: {new Date(cfg.last_sheet_sync_at).toLocaleString()}</p>}
      <SyncResultPanel res={res} remoteName="Sheet" testid="gw-sync-result" />
      {showLog && <SyncHistory log={log} remoteName="Sheet" loadDetail={getSheetSyncLogDetail} testid="gw-sheet-log" />}
    </div>
  );
}
